/**
 * PrLitReviewCyclePage
 *
 * A single PR Literature Review cycle (collection), reached from under the
 * Cycle group in the sidebar. The cycle's articles are topic-restricted to PR
 * by the shared context (based on the collection's type). Read-only: no
 * management action bar.
 *
 * Renders a `PrLitReviewStatusChart` at the top of the page showing this
 * cycle's status breakdown, followed by the standard toolbar and table.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import PrLiteratureReviewShell, {
  redirectAwayFromForbiddenCycle,
} from "@/components/pr-literature-review/PrLiteratureReviewShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  usePrLiteratureReviewPage,
  stripLocationColumns,
} from "@/components/pr-literature-review/usePrLiteratureReviewPage"
import { getColumnGroupsForViewMode } from "@/components/pr-literature-review/columns"
import { useResolvedCycle } from "@/components/pr-literature-review/useResolvedCycle"
import PrLitReviewStatusChart, {
  CollectionStatusEntry,
} from "@/components/journal-articles/PrLitReviewStatusChart"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Card, CardContent } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { SidebarOption } from "@/components/pr-literature-review/PrLiteratureReviewSidebar"
import {
  JournalArticleStatusCountsResponse,
  getJournalArticleStatusCounts,
} from "@/services/journalArticlesService"
import { useAuth } from "@/hooks/useAuth"

/** Title shown while the cycle is resolving. */
const LOADING_TITLE: string = "PR Literature Review Cycle"

/** Short description shown under the cycle name heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles in this PR Literature Review cycle"

/** The API topic slug for PR (parasite rate) articles. */
const PR_TOPIC_SLUG: string = "pr"

/**
 * The PR Literature Review cycle page.
 *
 * @returns The rendered page element.
 */
export default function PrLitReviewCyclePage(): React.JSX.Element {
  const navigate = useNavigate()
  const resolved = useResolvedCycle()

  // On an unresolvable cycle (missing / wrong type / inaccessible), redirect to
  // the overview with an explanatory toast.
  useEffect(() => {
    if (resolved.status === "not-found") {
      redirectAwayFromForbiddenCycle(navigate)
    }
  }, [resolved.status, navigate])

  if (resolved.status === "ready") {
    return (
      <PrLitReviewCycleReady
        collectionId={resolved.collection.id}
        collectionName={resolved.collection.collectionName}
      />
    )
  }

  // Loading or redirecting: keep the sidebar frame and show a spinner.
  return (
    <PrLiteratureReviewShell title={LOADING_TITLE}>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardContent className="flex flex-1 flex-col min-h-0">
          <LoadingSpinner variant="outline" loadingContent="Loading cycle..." />
        </CardContent>
      </Card>
    </PrLiteratureReviewShell>
  )
}

/**
 * Props for the ready-state inner component.
 */
interface PrLitReviewCycleReadyProps {
  /** The resolved collection id. */
  collectionId: number
  /** The resolved collection name. */
  collectionName: string
}

/**
 * The ready-state body: a status chart at the top, followed by the standard
 * table scoped to the resolved cycle. Separated so the hook calls only run when
 * the cycle is resolved (avoiding conditional hook issues).
 *
 * @param props - The resolved cycle props.
 * @returns The rendered table page element.
 */
function PrLitReviewCycleReady({
  collectionId,
  collectionName,
}: PrLitReviewCycleReadyProps): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const sidebarOption: SidebarOption = `collection-${collectionId}`
  const page = usePrLiteratureReviewPage({ sidebarOption })

  // Whether the status chart is expanded. Open by default; the user can collapse
  // it to give the article table more vertical room.
  const [chartOpen, setChartOpen] = useState<boolean>(true)

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  // Fetch this cycle's status counts for the chart. Clear any stale counts
  // immediately when the collection id changes so the chart hides until the new
  // data arrives (rather than briefly showing the previous cycle's chart).
  const [statusCounts, setStatusCounts] = useState<JournalArticleStatusCountsResponse | undefined>(
    undefined
  )
  useEffect(() => {
    setStatusCounts(undefined)
    let cancelled: boolean = false

    async function fetchCounts(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const result: JournalArticleStatusCountsResponse = await getJournalArticleStatusCounts(
          token,
          collectionId,
          PR_TOPIC_SLUG
        )
        if (!cancelled) {
          setStatusCounts(result)
        }
      } catch (err) {
        // The chart is a convenience, not core data, so a failure is logged
        // rather than surfaced as a page-level error. The chart simply stays
        // empty until the next load.
        console.error("Failed to fetch status counts for cycle:", err)
      }
    }

    void fetchCounts()
    return () => {
      cancelled = true
    }
  }, [getAccessToken, collectionId])

  // Build the single entry the chart component expects.
  const chartEntries: CollectionStatusEntry[] = useMemo(
    () => [
      {
        collectionId,
        collectionName,
        statusCounts,
      },
    ],
    [collectionId, collectionName, statusCounts]
  )

  return (
    <PrLiteratureReviewShell
      title={collectionName}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      {/* Collapsible status breakdown chart for this cycle. Hidden until the
          status counts have loaded, so switching cycles does not show an empty
          chart for the previous/not-yet-fetched data. */}
      {statusCounts !== undefined && (
        <Collapsible open={chartOpen} onOpenChange={setChartOpen} className="mb-4">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            <ChevronDown
              className={cn(
                "size-4 transition-transform duration-200",
                chartOpen ? "rotate-0" : "-rotate-90"
              )}
            />
            Status breakdown
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <PrLitReviewStatusChart entries={chartEntries} />
          </CollapsibleContent>
        </Collapsible>
      )}

      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={visibleColumns}
        columnGroupsForViewMode={getColumnGroupsForViewMode}
      />
    </PrLiteratureReviewShell>
  )
}
